import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  Group,
  Menu,
  Modal,
  MultiSelect,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { shellDrawerClassNames } from '../../../../app/shellDrawerClassNames';
import {
  IconDots,
  IconLayoutSidebarRight,
  IconMapPin,
  IconPictureInPicture,
  IconSubtask,
  IconTrash,
  IconUserPlus,
  IconX,
} from '@tabler/icons-react';
import {
  addUserAssignment,
  deleteUserAssignment,
  updateUserAssignmentStatus,
  type PlanningUserAssignment,
  type UserAssignmentRole,
} from '../../../../api/userAssignmentApis';
import { useModalContainer } from '../../../../contexts/ModalContainerContext';
import {
  ASSIGN_ROLE_OPTIONS,
  assignRoleColor,
  computeAssignDisabledReason,
  computeAssignPreview,
  type AssignLocationOption,
  type AssignTargetChip,
  type AssignTaskOption,
  type AssignUserOption,
  type AssignUsersDrawerMode,
  type AssignUsersScope,
} from './assignUsersTypes';
import {
  loadAssignUsersShell,
  saveAssignUsersShell,
  type AssignUsersShell,
} from './assignUsersShellPreference';
import { collapseRepeatedPathSegments } from './flattenAssignLocations';

const DRAWER_SURFACE = 'var(--app-drawer-surface, var(--mantine-color-gray-0))';
/** Raised section cards so Selections / Current assignees read against the grey drawer. */
const SECTION_CARD_STYLE: React.CSSProperties = {
  background: 'var(--mantine-color-body)',
};
const DRAWER_SIZE = 460;
/** Compact list shows this many targets before "+ N more". */
const TARGETS_INLINE_LIMIT = 3;
/** At this count, collapse to a single “N selected / View all” summary. */
const TARGETS_SUMMARY_THRESHOLD = 8;
/** Collapse current-assignees list above this count. */
const ASSIGNEES_COLLAPSE_AT = 3;
/** Nested above Task Details drawer (same pattern as predecessor picker). */
const MODAL_Z_INDEX = 1200;
const FLOATING_Z_INDEX = 1000;
const PANEL_WIDTH = 460;
const PANEL_HEIGHT = 640;
const MIN_PANEL_WIDTH = 360;
const MIN_PANEL_HEIGHT = 420;
const HEADER_HEIGHT = 56;
const MIN_PANEL_TOP = 8;
const MODAL_BODY_HEIGHT = 'min(70vh, 640px)';

export type AssignUsersPresentation = 'shell' | 'modal';

export interface AssignUsersDrawerProps {
  opened: boolean;
  onClose: () => void;
  projectId: string;
  mode: AssignUsersDrawerMode;
  /**
   * `shell` — Drawer/Float preference (Tools).
   * `modal` — stacked above Task Details (same pattern as predecessor picker).
   */
  presentation?: AssignUsersPresentation;
  /**
   * When true (Task Details entry), scope/targets are fixed from context —
   * only users and role can be changed.
   */
  targetsLocked?: boolean;
  /**
   * Two-way sync with Gantt assign checkboxes (bulk / Tools entry).
   * Task chips follow selection; selection-derived location chips do too.
   * Manually added locations stay drawer-only. Off when `targetsLocked`.
   */
  selectionSync?: boolean;
  /** Chips derived from current grid multi-select (task + location-type rows). */
  selectionTargets?: AssignTargetChip[];
  /**
   * Full grid checkbox selection (task row ids + location-type row ids).
   * Prefer functional updates via onSelectionTaskIdsChange to avoid stale snapshots.
   */
  selectionTaskIds?: string[];
  /**
   * Replace or functionally update the full grid checkbox set.
   * Prefer `(prev) => next` so rapid drawer edits don’t resurrect ids.
   */
  onSelectionTaskIdsChange?: (
    next: string[] | ((prev: string[]) => string[]),
  ) => void;
  /** User removed a selection-derived location chip — uncheck matching location rows. */
  onSelectionLocationIdRemove?: (locationId: string) => void;
  /**
   * Add location-type grid rows for these locationIds (checkbox sync).
   * Returns locationIds that had no matching location-type row (kept drawer-only).
   */
  onSelectionLocationIdsAdd?: (locationIds: string[]) => string[];
  /**
   * Clear grid checkboxes by scope: location-type rows, or everything that isn’t.
   * Use this instead of chip-id sets so orphans (no chip) are cleared too.
   */
  onSelectionClearScope?: (scope: AssignUsersScope) => void;
  /** Prefer task scope when opening from Task Details. */
  initialScope?: AssignUsersScope;
  initialTaskTargets?: AssignTargetChip[];
  userOptions: AssignUserOption[];
  locationOptions: AssignLocationOption[];
  /** Optional catalog for task search add. */
  taskCatalog?: AssignTaskOption[];
  /** Leaf tasks matching active grid column filters for “+ Add all N filtered tasks”.
   * Empty / omitted when no column filters are applied — CTA is hidden. */
  filterLeafTasks?: AssignTargetChip[];
  existingAssignments: PlanningUserAssignment[];
  onExistingAssignmentsChange?: (next: PlanningUserAssignment[]) => void;
  onAssignmentsMutated?: () => void | Promise<void>;
  usersLoading?: boolean;
  assignmentsLoading?: boolean;
}

function mergeTargetChips(base: AssignTargetChip[], extra: AssignTargetChip[]): AssignTargetChip[] {
  const map = new Map(base.map((t) => [t.id, t] as const));
  for (const t of extra) {
    if (!t.id) continue;
    const existing = map.get(t.id);
    if (!existing) {
      map.set(t.id, t);
      continue;
    }
    // Prefer base metadata; union sourceTaskIds so remove/clear never loses grid row ids.
    const mergedIds = [
      ...new Set([...(existing.sourceTaskIds ?? []), ...(t.sourceTaskIds ?? [])]),
    ];
    if (mergedIds.length > 0) {
      map.set(t.id, {
        ...existing,
        sourceTaskIds: mergedIds,
      });
    }
  }
  return [...map.values()];
}

function newAssignmentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `asg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function roleLabel(role: string): string {
  const hit = ASSIGN_ROLE_OPTIONS.find((r) => r.value === role);
  return hit?.label ?? role;
}

/** Secondary path under the target label (deduped consecutive segments). */
function targetSecondaryPath(t: AssignTargetChip): string | undefined {
  const raw = t.breadcrumb?.trim();
  if (!raw) return undefined;
  return collapseRepeatedPathSegments(raw) || undefined;
}

function targetFullPath(t: AssignTargetChip): string {
  const path = targetSecondaryPath(t);
  return path ? `${t.label} · ${path}` : t.label;
}

function TruncatedPath({ path }: { path: string }) {
  return (
    <Text
      size="xs"
      c="dimmed"
      lh={1.25}
      title={path}
      style={{
        display: 'block',
        minWidth: 0,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {path}
    </Text>
  );
}

function clampPanelPosition(
  pos: { x: number; y: number },
  size: { width: number; height: number },
): { x: number; y: number } {
  const maxX = typeof window !== 'undefined' ? Math.max(0, window.innerWidth - size.width) : pos.x;
  const maxY =
    typeof window !== 'undefined'
      ? Math.max(MIN_PANEL_TOP, window.innerHeight - HEADER_HEIGHT)
      : pos.y;
  return {
    x: Math.max(0, Math.min(pos.x, maxX)),
    y: Math.max(MIN_PANEL_TOP, Math.min(pos.y, maxY)),
  };
}

function defaultFloatPosition(size: { width: number; height: number }) {
  if (typeof window === 'undefined') return { x: 80, y: 80 };
  return clampPanelPosition(
    {
      x: Math.max(24, window.innerWidth - size.width - 24),
      y: 72,
    },
    size,
  );
}

function roleLabelPlural(role: string, count: number): string {
  const base = roleLabel(role);
  if (count === 1) return base;
  return `${base}s`;
}

export function AssignUsersDrawer({
  opened,
  onClose,
  projectId,
  mode,
  presentation = 'shell',
  targetsLocked = false,
  selectionSync = false,
  selectionTargets = [],
  selectionTaskIds: _selectionTaskIds = [],
  onSelectionTaskIdsChange,
  onSelectionLocationIdRemove,
  onSelectionLocationIdsAdd,
  onSelectionClearScope,
  initialScope = 'location',
  initialTaskTargets = [],
  userOptions,
  locationOptions,
  taskCatalog = [],
  filterLeafTasks = [],
  existingAssignments,
  onExistingAssignmentsChange,
  onAssignmentsMutated,
  usersLoading = false,
  assignmentsLoading = false,
}: AssignUsersDrawerProps) {
  const modalContainer = useModalContainer();
  const [shell, setShell] = useState<AssignUsersShell>(() => loadAssignUsersShell());
  const [scope, setScope] = useState<AssignUsersScope>(initialScope);
  const [targetsByScope, setTargetsByScope] = useState<{
    location: AssignTargetChip[];
    task: AssignTargetChip[];
  }>({ location: [], task: [] });
  /** Location chips added in-drawer while checkbox sync is on (not from grid selection). */
  const [manualLocationTargets, setManualLocationTargets] = useState<AssignTargetChip[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [role, setRole] = useState<UserAssignmentRole | null>('capturer');
  const [locationSearch, setLocationSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);
  /** When true, show every target (past inline / summary collapse). */
  const [targetsShowAll, setTargetsShowAll] = useState(false);
  /** When true, expand current-assignees past the compact collapse. */
  const [assigneesExpanded, setAssigneesExpanded] = useState(false);

  const isModal = presentation === 'modal';
  const dropdownZIndex = (isModal ? MODAL_Z_INDEX : FLOATING_Z_INDEX) + 100;
  const selectionSyncActive = selectionSync && !targetsLocked;

  const selectionTaskTargets = useMemo(
    () => selectionTargets.filter((t) => t.kind === 'task'),
    [selectionTargets],
  );
  const selectionLocationTargets = useMemo(
    () => selectionTargets.filter((t) => t.kind === 'location'),
    [selectionTargets],
  );

  const locationTargets = useMemo(() => {
    if (!selectionSyncActive) return targetsByScope.location;
    // Grid selection is source of truth. Manual only holds locations with no
    // location-type row on the grid (cannot be checkbox-synced).
    return mergeTargetChips(selectionLocationTargets, manualLocationTargets);
  }, [
    selectionSyncActive,
    targetsByScope.location,
    selectionLocationTargets,
    manualLocationTargets,
  ]);

  const taskTargets = useMemo(() => {
    if (!selectionSyncActive) return targetsByScope.task;
    return selectionTaskTargets;
  }, [selectionSyncActive, targetsByScope.task, selectionTaskTargets]);

  /** Active scope’s targets only — Location/Task lists are kept separately. */
  const targets = scope === 'location' ? locationTargets : taskTargets;

  // Drop manual location chips once they appear in grid selection (or they would
  // shadow unchecks and break Locations ↔ grid sync).
  useEffect(() => {
    if (!selectionSyncActive) return;
    if (manualLocationTargets.length === 0) return;
    const selectedLocIds = new Set(selectionLocationTargets.map((t) => t.id));
    if (selectedLocIds.size === 0) return;
    setManualLocationTargets((prev) => {
      const next = prev.filter((t) => !selectedLocIds.has(t.id));
      return next.length === prev.length ? prev : next;
    });
  }, [selectionSyncActive, selectionLocationTargets, manualLocationTargets.length]);

  const [floatSize, setFloatSize] = useState({ width: PANEL_WIDTH, height: PANEL_HEIGHT });
  const [floatPosition, setFloatPosition] = useState(() =>
    defaultFloatPosition({ width: PANEL_WIDTH, height: PANEL_HEIGHT }),
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
  });
  const prevOpenedRef = useRef(false);
  const floatOpenedRef = useRef(false);
  const suppressAutoScopeRef = useRef(false);

  useEffect(() => {
    if (!opened) {
      prevOpenedRef.current = false;
      return;
    }
    const justOpened = !prevOpenedRef.current;
    prevOpenedRef.current = true;

    // While checkbox-synced, selectionTargets drive chips — don't wipe form state
    // on every selection change.
    if (!justOpened && selectionSyncActive) return;

    const seed =
      selectionSyncActive && selectionTargets.length > 0
        ? selectionTargets
        : initialTaskTargets;
    const location = seed.filter((t) => t.kind === 'location');
    const task = seed.filter((t) => t.kind === 'task');
    setTargetsByScope({ location, task });
    setManualLocationTargets([]);
    // Prefer a scope that actually has chips when only one side arrives.
    if (task.length > 0 && location.length === 0) {
      setScope('task');
    } else if (location.length > 0 && task.length === 0) {
      setScope('location');
    } else {
      setScope(initialScope);
    }
    if (justOpened) {
      suppressAutoScopeRef.current = false;
      setSelectedUserIds([]);
      setRole('capturer');
      setLocationSearch('');
      setTaskSearch('');
      setTargetsShowAll(false);
      setAssigneesExpanded(false);
    }
  }, [opened, initialScope, initialTaskTargets, selectionSyncActive, selectionTargets]);

  // When checkbox selection empties the active scope, jump to the side that still
  // has chips — but never after an intentional Clear (stay on the empty tab so
  // the user can re-add). suppressAutoScopeRef is set by clearAllTargets.
  useEffect(() => {
    if (!opened) {
      suppressAutoScopeRef.current = false;
      return;
    }
    if (!selectionSyncActive) return;
    if (suppressAutoScopeRef.current) return;
    setScope((current) => {
      if (current === 'location' && locationTargets.length === 0 && taskTargets.length > 0) {
        return 'task';
      }
      if (current === 'task' && taskTargets.length === 0 && locationTargets.length > 0) {
        return 'location';
      }
      return current;
    });
  }, [opened, selectionSyncActive, locationTargets.length, taskTargets.length]);

  useEffect(() => {
    setTargetsShowAll(false);
  }, [scope, targets.length]);

  useEffect(() => {
    if (opened && !floatOpenedRef.current && shell === 'floating') {
      const size = { width: PANEL_WIDTH, height: PANEL_HEIGHT };
      setFloatSize(size);
      setFloatPosition(defaultFloatPosition(size));
    }
    floatOpenedRef.current = opened;
  }, [opened, shell]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d.isDragging) return;
      setFloatPosition(
        clampPanelPosition(
          {
            x: d.startLeft + (e.clientX - d.startX),
            y: d.startTop + (e.clientY - d.startY),
          },
          floatSize,
        ),
      );
    };
    const onUp = () => {
      dragRef.current.isDragging = false;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, floatSize]);

  const preview = useMemo(
    () =>
      computeAssignPreview({
        scope,
        targets,
        userIds: selectedUserIds,
        role,
      }),
    [scope, targets, selectedUserIds, role],
  );

  const confirmSummary = useMemo(() => {
    if (preview.willCreate <= 0 || !preview.role) return null;
    const roleWord = roleLabelPlural(preview.role, preview.userCount);
    const scopeWord =
      scope === 'location'
        ? preview.targetCount === 1
          ? 'location'
          : 'locations'
        : preview.targetCount === 1
          ? 'task'
          : 'tasks';
    const userWord = preview.userCount === 1 ? 'user' : 'users';
    let text = `Assigning ${preview.userCount} ${userWord} as ${roleWord} to ${preview.targetCount} ${scopeWord} (this tab only).`;
    if (preview.skippedNoLocation > 0) {
      text += ` Skipping ${preview.skippedNoLocation} without location.`;
    }
    return text;
  }, [preview, scope]);

  /** Other tab still has selections — this submit never includes them. */
  const otherScopePendingNote = useMemo(() => {
    if (targetsLocked) return null;
    if (scope === 'location' && taskTargets.length > 0) {
      const n = taskTargets.length;
      return `This assign is locations only. ${n} task${n === 1 ? '' : 's'} stay on the Tasks tab for a separate assign.`;
    }
    if (scope === 'task' && locationTargets.length > 0) {
      const n = locationTargets.length;
      return `This assign is tasks only. ${n} location${n === 1 ? '' : 's'} stay on the Locations tab for a separate assign.`;
    }
    return null;
  }, [targetsLocked, scope, taskTargets.length, locationTargets.length]);

  const assignCtaLabel = useMemo(() => {
    const n = preview.targetCount;
    if (n <= 0) return 'Assign';
    if (scope === 'location') {
      return n === 1 ? 'Assign to 1 location' : `Assign to ${n} locations`;
    }
    return n === 1 ? 'Assign to 1 task' : `Assign to ${n} tasks`;
  }, [preview.targetCount, scope]);

  const userFieldError = useMemo(() => {
    if (targets.length === 0) return null;
    if (selectedUserIds.length > 0) return null;
    return 'Select at least one user.';
  }, [targets.length, selectedUserIds.length]);

  const visibleTargets = useMemo(() => {
    if (targetsShowAll) return targets;
    if (targets.length >= TARGETS_SUMMARY_THRESHOLD) return [];
    return targets.slice(0, TARGETS_INLINE_LIMIT);
  }, [targets, targetsShowAll]);

  const hiddenTargetCount = useMemo(() => {
    if (targetsShowAll) return 0;
    if (targets.length >= TARGETS_SUMMARY_THRESHOLD) return targets.length;
    return Math.max(0, targets.length - TARGETS_INLINE_LIMIT);
  }, [targets, targetsShowAll]);

  const showTargetsSummary =
    !targetsShowAll && targets.length >= TARGETS_SUMMARY_THRESHOLD;

  const disabledReason = useMemo(
    () =>
      computeAssignDisabledReason({
        projectId,
        scope,
        targets,
        userIds: selectedUserIds,
        role,
        submitting,
      }),
    [projectId, scope, targets, selectedUserIds, role, submitting],
  );

  const canSubmit = disabledReason == null;

  const relevantExisting = useMemo(() => {
    if (targets.length === 0) return [];

    const taskTargetIds = new Set(
      targets.filter((t) => t.kind === 'task').map((t) => t.id),
    );
    const locationIdsFromTargets = new Set<string>();
    for (const t of targets) {
      if (t.kind === 'location') locationIdsFromTargets.add(t.id);
      if (t.kind === 'task' && t.locationId) locationIdsFromTargets.add(t.locationId);
    }

    const taskLabelById = new Map<string, string>();
    for (const t of targets) {
      if (t.kind === 'task') taskLabelById.set(t.id, t.label);
    }
    const multiTaskTargets = taskTargetIds.size > 1;

    const locLabelById = new Map<string, string>();
    for (const t of targets) {
      if (t.kind === 'location') {
        locLabelById.set(t.id, t.label);
      } else if (t.locationId && t.breadcrumb) {
        const leaf = t.breadcrumb.split(' › ').pop()?.trim();
        locLabelById.set(t.locationId, leaf || t.locationId);
      }
    }
    for (const opt of locationOptions) {
      if (!locLabelById.has(opt.value)) locLabelById.set(opt.value, opt.label);
    }

    type RowWithSource = PlanningUserAssignment & { sourceLabel: string };
    const out: RowWithSource[] = [];
    for (const a of existingAssignments) {
      let sourceLabel: string | null = null;
      if (a.scopeType === 'task' && a.taskId && taskTargetIds.has(String(a.taskId))) {
        if (multiTaskTargets) {
          const name =
            taskLabelById.get(String(a.taskId))?.trim() || String(a.taskId);
          sourceLabel = name;
        } else {
          sourceLabel = 'This task';
        }
      } else if (
        a.scopeType === 'location' &&
        a.locationId &&
        locationIdsFromTargets.has(String(a.locationId))
      ) {
        const name = locLabelById.get(String(a.locationId)) || String(a.locationId);
        sourceLabel = taskTargetIds.size > 0 ? `Inherited from ${name}` : `Location · ${name}`;
      }
      if (!sourceLabel) continue;
      out.push({ ...a, sourceLabel });
    }
    return out;
  }, [existingAssignments, targets, locationOptions]);

  const uniqueAssigneePeopleCount = useMemo(() => {
    const ids = new Set(relevantExisting.map((a) => String(a.userId)));
    return ids.size;
  }, [relevantExisting]);

  const currentAssigneesSummary = useMemo(() => {
    const n = relevantExisting.length;
    if (n === 0) return '';
    const assignmentLabel = n === 1 ? '1 assignment' : `${n} assignments`;
    if (uniqueAssigneePeopleCount === n) return assignmentLabel;
    const peopleLabel =
      uniqueAssigneePeopleCount === 1
        ? '1 person'
        : `${uniqueAssigneePeopleCount} people`;
    return `${assignmentLabel} · ${peopleLabel}`;
  }, [relevantExisting.length, uniqueAssigneePeopleCount]);

  const visibleAssignees = useMemo(() => {
    if (assigneesExpanded || relevantExisting.length <= ASSIGNEES_COLLAPSE_AT) {
      return relevantExisting;
    }
    return relevantExisting.slice(0, ASSIGNEES_COLLAPSE_AT);
  }, [assigneesExpanded, relevantExisting]);

  const locationSearchHits = useMemo(() => {
    const q = locationSearch.trim().toLowerCase();
    if (!q) return [];
    const selectedIds = new Set(locationTargets.map((t) => t.id));
    return locationOptions
      .filter((o) => {
        if (selectedIds.has(o.value)) return false;
        const hay = `${o.label} ${o.breadcrumb ?? ''} ${o.value}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  }, [locationOptions, locationSearch, locationTargets]);

  const taskSearchHits = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    if (!q) return [];
    return taskCatalog
      .filter((t) => {
        const hay = `${t.label} ${t.breadcrumb ?? ''} ${t.id}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  }, [taskCatalog, taskSearch]);

  const addTargets = useCallback(
    (next: AssignTargetChip[]) => {
      const locationChips = next.filter((t) => t.kind === 'location' && t.id && t.locationId);
      const taskChips = next.filter((t) => t.kind === 'task' && t.id && t.locationId);

      if (selectionSyncActive) {
        if (taskChips.length > 0 && onSelectionTaskIdsChange) {
          const addIds = taskChips.map((t) => t.id);
          onSelectionTaskIdsChange((prev) => {
            const merged = new Set(prev);
            for (const id of addIds) merged.add(id);
            return [...merged];
          });
        }
        if (locationChips.length > 0) {
          // Prefer grid checkbox sync (same as Tasks). Only keep drawer-only
          // leftovers that have no location-type row on the gantt.
          const locIds = locationChips.map((t) => t.locationId);
          const unresolved = new Set(
            onSelectionLocationIdsAdd?.(locIds) ?? locIds,
          );
          const manualOnly = locationChips.filter((t) => unresolved.has(t.locationId));
          if (manualOnly.length > 0) {
            setManualLocationTargets((prev) => mergeTargetChips(prev, manualOnly));
          }
        }
        return;
      }

      setTargetsByScope((prev) => {
        const locationMap = new Map(prev.location.map((t) => [t.id, t]));
        const taskMap = new Map(prev.task.map((t) => [t.id, t]));
        for (const t of locationChips) locationMap.set(t.id, t);
        for (const t of taskChips) taskMap.set(t.id, t);
        return {
          location: [...locationMap.values()],
          task: [...taskMap.values()],
        };
      });
    },
    [
      selectionSyncActive,
      onSelectionTaskIdsChange,
      onSelectionLocationIdsAdd,
    ],
  );

  const removeTarget = useCallback(
    (chip: AssignTargetChip) => {
      if (targetsLocked) return;

      if (selectionSyncActive) {
        if (chip.kind === 'task') {
          onSelectionTaskIdsChange?.((prev) => prev.filter((id) => id !== chip.id));
          return;
        }
        // Location chips use locationId as id — uncheck via tracked grid row ids,
        // and always fall back to locationId reverse-lookup.
        const sourceIds = new Set(chip.sourceTaskIds ?? []);
        if (sourceIds.size > 0) {
          onSelectionTaskIdsChange?.((prev) => prev.filter((id) => !sourceIds.has(id)));
        }
        onSelectionLocationIdRemove?.(chip.id);
        setManualLocationTargets((prev) => prev.filter((t) => t.id !== chip.id));
        return;
      }

      setTargetsByScope((prev) => {
        if (chip.kind === 'location') {
          return {
            ...prev,
            location: prev.location.filter((t) => t.id !== chip.id),
          };
        }
        return {
          ...prev,
          task: prev.task.filter((t) => t.id !== chip.id),
        };
      });
    },
    [
      targetsLocked,
      selectionSyncActive,
      onSelectionTaskIdsChange,
      onSelectionLocationIdRemove,
    ],
  );

  const clearAllTargets = useCallback(() => {
    if (targetsLocked) return;

    if (selectionSyncActive) {
      // Stay on this tab after Clear so the user can re-add without auto-jump.
      suppressAutoScopeRef.current = true;
      if (onSelectionClearScope) {
        onSelectionClearScope(scope);
      } else if (scope === 'task') {
        const taskChipIds = new Set(selectionTaskTargets.map((t) => t.id));
        onSelectionTaskIdsChange?.((prev) => prev.filter((id) => !taskChipIds.has(id)));
      } else {
        const locationRowIds = new Set<string>();
        for (const t of selectionLocationTargets) {
          for (const id of t.sourceTaskIds ?? []) locationRowIds.add(id);
        }
        if (locationRowIds.size > 0) {
          onSelectionTaskIdsChange?.((prev) => prev.filter((id) => !locationRowIds.has(id)));
        }
        for (const loc of selectionLocationTargets) {
          onSelectionLocationIdRemove?.(loc.id);
        }
      }
      if (scope === 'location') setManualLocationTargets([]);
      return;
    }

    setTargetsByScope((prev) =>
      scope === 'location'
        ? { ...prev, location: [] }
        : { ...prev, task: [] },
    );
  }, [
    targetsLocked,
    selectionSyncActive,
    scope,
    selectionTaskTargets,
    selectionLocationTargets,
    onSelectionTaskIdsChange,
    onSelectionLocationIdRemove,
    onSelectionClearScope,
  ]);

  const addLocationFromSearch = useCallback(
    (opt: { value: string; label: string; breadcrumb?: string }) => {
      addTargets([
        {
          kind: 'location',
          id: opt.value,
          label: opt.label,
          locationId: opt.value,
          breadcrumb: opt.breadcrumb,
        },
      ]);
      setLocationSearch('');
    },
    [addTargets],
  );

  const addFromFilter = useCallback(() => {
    const withLoc = filterLeafTasks.filter((t) => t.locationId.trim() !== '');
    addTargets(withLoc);
    const skipped = filterLeafTasks.length - withLoc.length;
    notifications.show({
      title: 'Selections updated',
      message:
        skipped > 0
          ? `Added ${withLoc.length} tasks; skipped ${skipped} without location.`
          : `Added ${withLoc.length} tasks from the current filter.`,
      color: 'teal',
    });
  }, [filterLeafTasks, addTargets]);

  const addTaskFromSearch = useCallback(
    (task: AssignTaskOption) => {
      if (!task.locationId) {
        notifications.show({
          title: 'Missing location',
          message: 'This task has no locationId and cannot be assigned.',
          color: 'orange',
        });
        return;
      }
      addTargets([
        {
          kind: 'task',
          id: task.id,
          label: task.label,
          locationId: task.locationId,
          breadcrumb: task.breadcrumb,
        },
      ]);
      setTaskSearch('');
    },
    [addTargets],
  );

  const handleScopeChange = (value: string) => {
    if (targetsLocked) return;
    suppressAutoScopeRef.current = false;
    setScope(value === 'task' ? 'task' : 'location');
    setTargetsShowAll(false);
  };

  const handleShellChange = (value: string) => {
    if (value !== 'drawer' && value !== 'floating') return;
    setShell(value);
    saveAssignUsersShell(value);
    if (value === 'floating') {
      const size = { width: PANEL_WIDTH, height: PANEL_HEIGHT };
      setFloatSize(size);
      setFloatPosition(defaultFloatPosition(size));
    }
  };

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: floatPosition.x,
      startTop: floatPosition.y,
    };
    setIsDragging(true);
  };

  const patchLocalAssignments = useCallback(
    (updater: (prev: PlanningUserAssignment[]) => PlanningUserAssignment[]) => {
      onExistingAssignmentsChange?.(updater(existingAssignments));
    },
    [existingAssignments, onExistingAssignmentsChange],
  );

  const finishAfterSuccessfulAssign = useCallback(
    (submittedScope: AssignUsersScope) => {
      setSelectedUserIds([]);

      // Locked single-task modal (assignees cell / task details): close after success.
      if (targetsLocked) {
        onClose();
        return;
      }

      // Drop what we just assigned so it doesn’t keep warning as “pending”.
      if (selectionSyncActive) {
        onSelectionClearScope?.(submittedScope);
        if (submittedScope === 'location') setManualLocationTargets([]);
      } else {
        setTargetsByScope((prev) =>
          submittedScope === 'location'
            ? { ...prev, location: [] }
            : { ...prev, task: [] },
        );
      }

      const otherCount =
        submittedScope === 'location' ? taskTargets.length : locationTargets.length;
      if (otherCount > 0) {
        // Move to the remaining tab so the next assign is obvious.
        suppressAutoScopeRef.current = true;
        setScope(submittedScope === 'location' ? 'task' : 'location');
      }
    },
    [
      selectionSyncActive,
      onSelectionClearScope,
      targetsLocked,
      onClose,
      taskTargets.length,
      locationTargets.length,
    ],
  );

  const handleAssign = useCallback(async () => {
    if (!canSubmit || !role) return;
    const validTargets = targets.filter((t) => t.locationId.trim() !== '');
    const submittedScope = scope;
    setSubmitting(true);
    let ok = 0;
    let failed = 0;

    try {
      if (mode === 'mock') {
        const created: PlanningUserAssignment[] = [];
        for (const target of validTargets) {
          for (const userId of selectedUserIds) {
            const userLabel =
              userOptions.find((u) => u.value === userId)?.label ?? userId;
            created.push({
              id: newAssignmentId(),
              projectId: projectId || 'mock-project',
              scopeType: submittedScope,
              locationId: target.locationId,
              taskId: submittedScope === 'task' ? target.id : null,
              userId,
              userName: userLabel,
              role,
              status: 'active',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
            ok += 1;
          }
        }
        patchLocalAssignments((prev) => [...created, ...prev]);
        notifications.show({
          title: 'Assignments created (preview)',
          message: `Mock-created ${ok} assignment${ok === 1 ? '' : 's'} for this tab only. No API calls.`,
          color: 'teal',
        });
        finishAfterSuccessfulAssign(submittedScope);
        return;
      }

      for (const target of validTargets) {
        for (const userId of selectedUserIds) {
          try {
            await addUserAssignment(
              {
                id: newAssignmentId(),
                projectId,
                scopeType: submittedScope,
                locationId: target.locationId,
                taskId: submittedScope === 'task' ? target.id : null,
                userId,
                role,
              },
              { silent: true },
            );
            ok += 1;
          } catch {
            failed += 1;
          }
        }
      }

      await onAssignmentsMutated?.();

      if (failed === 0) {
        notifications.show({
          title: 'Assignments saved',
          message: `Created ${ok} assignment${ok === 1 ? '' : 's'} for this tab only.`,
          color: 'teal',
        });
        finishAfterSuccessfulAssign(submittedScope);
      } else {
        notifications.show({
          title: 'Assignments partially saved',
          message: `Created ${ok}, failed ${failed}. Selections kept so you can retry.`,
          color: 'orange',
        });
        setSelectedUserIds([]);
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    role,
    targets,
    mode,
    selectedUserIds,
    userOptions,
    projectId,
    scope,
    patchLocalAssignments,
    onAssignmentsMutated,
    finishAfterSuccessfulAssign,
  ]);

  const handleToggleStatus = useCallback(
    async (row: PlanningUserAssignment) => {
      const nextStatus = row.status === 'active' ? 'inactive' : 'active';
      setStatusBusyId(row.id);
      try {
        if (mode === 'mock') {
          patchLocalAssignments((prev) =>
            prev.map((a) =>
              a.id === row.id
                ? { ...a, status: nextStatus, updatedAt: new Date().toISOString() }
                : a,
            ),
          );
          return;
        }
        await updateUserAssignmentStatus(
          { assignmentId: row.id, status: nextStatus },
          { silent: true },
        );
        await onAssignmentsMutated?.();
        notifications.show({
          title: 'Assignment updated',
          message: `Marked ${nextStatus}.`,
          color: 'teal',
        });
      } catch {
        notifications.show({
          title: 'Update failed',
          message: 'Could not update assignment status.',
          color: 'red',
        });
      } finally {
        setStatusBusyId(null);
      }
    },
    [mode, patchLocalAssignments, onAssignmentsMutated],
  );

  const handleDeleteAssignment = useCallback(
    async (row: PlanningUserAssignment) => {
      const label = row.userName || row.userId;
      setStatusBusyId(row.id);
      try {
        if (mode === 'mock') {
          patchLocalAssignments((prev) => prev.filter((a) => a.id !== row.id));
          notifications.show({
            title: 'Assignment deleted',
            message: `Removed ${label}.`,
            color: 'teal',
          });
          return;
        }
        await deleteUserAssignment(row.id, { silent: true });
        patchLocalAssignments((prev) => prev.filter((a) => a.id !== row.id));
        await onAssignmentsMutated?.();
        notifications.show({
          title: 'Assignment deleted',
          message: `Removed ${label}.`,
          color: 'teal',
        });
      } catch {
        notifications.show({
          title: 'Delete failed',
          message: 'Could not delete user assignment.',
          color: 'red',
        });
      } finally {
        setStatusBusyId(null);
      }
    },
    [mode, patchLocalAssignments, onAssignmentsMutated],
  );

  const shellMenu = !isModal ? (
    <Menu shadow="md" width={220} position="bottom-end" withinPortal zIndex={dropdownZIndex}>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label="More options"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <IconDots size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Display</Menu.Label>
        {shell === 'drawer' ? (
          <Menu.Item
            leftSection={<IconPictureInPicture size={14} />}
            onClick={() => handleShellChange('floating')}
          >
            Open as floating panel
          </Menu.Item>
        ) : (
          <Menu.Item
            leftSection={<IconLayoutSidebarRight size={14} />}
            onClick={() => handleShellChange('drawer')}
          >
            Dock as drawer
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  ) : null;

  const headerActions = shellMenu ? (
    <Group
      gap={4}
      wrap="nowrap"
      align="flex-start"
      style={{ flexShrink: 0, paddingTop: 1 }}
    >
      {shellMenu}
    </Group>
  ) : null;

  const titleBlock = (
    <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
      <Group gap={8} wrap="nowrap" align="center">
        <IconUserPlus size={18} stroke={1.6} style={{ flexShrink: 0 }} />
        <Text fw={700} size="sm" truncate>
          Assign users
        </Text>
        {mode === 'mock' ? (
          <Badge size="xs" variant="light" color="grape">
            Preview
          </Badge>
        ) : null}
      </Group>
      <Text size="xs" c="dimmed" fw={400}>
        {targetsLocked
          ? scope === 'location'
            ? 'Assign users to this location'
            : 'Assign users to this task'
          : 'Assign users to tasks or locations'}
      </Text>
    </Stack>
  );

  const targetRow = (t: AssignTargetChip) => {
    const path = targetSecondaryPath(t);
    const chipTitle = targetFullPath(t);
    return (
      <Group
        key={`${t.kind}:${t.id}`}
        gap={8}
        wrap="nowrap"
        justify="space-between"
        align="flex-start"
        title={chipTitle}
        py={4}
        style={{ minWidth: 0 }}
      >
        <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
          <Text
            size="sm"
            fw={600}
            lh={1.25}
            style={{
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t.label}
          </Text>
          {path ? <TruncatedPath path={path} /> : null}
        </Stack>
        {!targetsLocked ? (
          <UnstyledButton
            type="button"
            aria-label={`Remove ${t.label}`}
            onClick={() => removeTarget(t)}
            style={{ display: 'inline-flex', lineHeight: 0, flexShrink: 0, paddingTop: 2 }}
          >
            <IconX size={14} />
          </UnstyledButton>
        ) : null}
      </Group>
    );
  };

  const scrollBody = (
    <Stack gap="sm">
      {!targetsLocked ? (
        <SegmentedControl
          fullWidth
          size="xs"
          value={scope}
          onChange={handleScopeChange}
          data={[
            {
              value: 'location',
              label: (
                <Group gap={6} justify="center">
                  <IconMapPin size={14} />
                  <span>
                    Location
                    {locationTargets.length > 0
                      ? ` (${locationTargets.length})`
                      : ''}
                  </span>
                </Group>
              ),
            },
            {
              value: 'task',
              label: (
                <Group gap={6} justify="center">
                  <IconSubtask size={14} />
                  <span>
                    Task
                    {taskTargets.length > 0
                      ? ` (${taskTargets.length})`
                      : ''}
                  </span>
                </Group>
              ),
            },
          ]}
        />
      ) : null}

      <Paper withBorder radius="md" p="sm" style={SECTION_CARD_STYLE}>
        <Group gap={6} mb={12} align="center" justify="space-between" wrap="nowrap">
          <Group gap={6} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            {targetsLocked ? (
              scope === 'location' ? (
                <IconMapPin size={14} stroke={1.6} />
              ) : (
                <IconSubtask size={14} stroke={1.6} />
              )
            ) : null}
            <Text size="xs" fw={600}>
              {targetsLocked
                ? scope === 'location'
                  ? 'Location'
                  : 'Task'
                : 'Selections'}
            </Text>
          </Group>
          {!targetsLocked && targets.length > 0 ? (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={clearAllTargets}
            >
              Clear
            </Button>
          ) : null}
        </Group>
        {targets.length === 0 ? (
          <Text size="xs" c="dimmed" mb={targetsLocked ? 0 : 8}>
            {targetsLocked
              ? 'This item has no locationId, so it cannot be assigned yet.'
              : scope === 'location'
                ? 'Search and click a location to add it.'
                : 'Add the selected task, filtered leaves, or search for a task.'}
          </Text>
        ) : (
          <Box mb={targetsLocked ? 0 : 8}>
            {showTargetsSummary ? (
              <Group justify="space-between" wrap="nowrap" align="center" py={4}>
                <Text size="sm" fw={500}>
                  {targets.length} {scope === 'location' ? 'locations' : 'tasks'} selected
                </Text>
                <UnstyledButton type="button" onClick={() => setTargetsShowAll(true)}>
                  <Text size="xs" c="blue" fw={600}>
                    View all
                  </Text>
                </UnstyledButton>
              </Group>
            ) : (
              <Stack gap={0}>
                {visibleTargets.map(targetRow)}
                {hiddenTargetCount > 0 ? (
                  <UnstyledButton
                    type="button"
                    onClick={() => setTargetsShowAll(true)}
                    style={{ alignSelf: 'flex-start', paddingTop: 4, paddingBottom: 2 }}
                  >
                    <Text size="xs" c="dimmed">
                      + {hiddenTargetCount} more
                    </Text>
                  </UnstyledButton>
                ) : null}
                {targetsShowAll && targets.length > TARGETS_INLINE_LIMIT ? (
                  <UnstyledButton
                    type="button"
                    onClick={() => setTargetsShowAll(false)}
                    style={{ alignSelf: 'flex-start', paddingTop: 4, paddingBottom: 2 }}
                  >
                    <Text size="xs" c="dimmed">
                      Show less
                    </Text>
                  </UnstyledButton>
                ) : null}
              </Stack>
            )}
          </Box>
        )}

        {!targetsLocked && scope === 'location' ? (
          <Stack gap={6}>
            <TextInput
              size="xs"
              label="Search locations"
              placeholder="Name, path, or id"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.currentTarget.value)}
            />
            {locationSearchHits.length > 0 ? (
              <Stack gap={2}>
                {locationSearchHits.map((opt) => {
                  const path = opt.breadcrumb?.trim()
                    ? collapseRepeatedPathSegments(opt.breadcrumb.trim())
                    : undefined;
                  const hitTitle = path ? `${opt.label} · ${path}` : opt.label;
                  return (
                    <UnstyledButton
                      key={opt.value}
                      type="button"
                      title={hitTitle}
                      onClick={() => addLocationFromSearch(opt)}
                      style={{
                        textAlign: 'left',
                        padding: '6px 8px',
                        borderRadius: 6,
                        border: '1px solid var(--mantine-color-default-border)',
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      <Text
                        size="xs"
                        fw={600}
                        style={{
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {opt.label}
                      </Text>
                      {path ? <TruncatedPath path={path} /> : null}
                    </UnstyledButton>
                  );
                })}
              </Stack>
            ) : locationSearch.trim() ? (
              <Text size="xs" c="dimmed">
                No matching locations
              </Text>
            ) : null}
          </Stack>
        ) : null}

        {!targetsLocked && scope === 'task' ? (
          <Stack gap={6}>
            {filterLeafTasks.length > 0 ? (
              <Button size="xs" variant="light" onClick={addFromFilter}>
                {`+ Add all ${filterLeafTasks.length} filtered tasks`}
              </Button>
            ) : null}
            <TextInput
              size="xs"
              label="Search tasks"
              placeholder="Name, parent path, or id"
              value={taskSearch}
              onChange={(e) => setTaskSearch(e.currentTarget.value)}
            />
            {taskSearchHits.length > 0 ? (
              <Stack gap={2}>
                {taskSearchHits.map((t) => {
                  const path = t.breadcrumb?.trim()
                    ? collapseRepeatedPathSegments(t.breadcrumb.trim())
                    : undefined;
                  const hitTitle = path ? `${t.label} · ${path}` : t.label;
                  return (
                    <UnstyledButton
                      key={t.id}
                      type="button"
                      title={hitTitle}
                      onClick={() => addTaskFromSearch(t)}
                      style={{
                        textAlign: 'left',
                        padding: '6px 8px',
                        borderRadius: 6,
                        border: '1px solid var(--mantine-color-default-border)',
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      <Text
                        size="xs"
                        fw={600}
                        style={{
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t.label}
                      </Text>
                      {path ? <TruncatedPath path={path} /> : null}
                      {!t.locationId ? (
                        <Text size="xs" c="orange">
                          No location — cannot assign
                        </Text>
                      ) : null}
                    </UnstyledButton>
                  );
                })}
              </Stack>
            ) : null}
          </Stack>
        ) : null}
      </Paper>

      {targets.length > 0 ? (
        <>
          <Paper withBorder radius="md" p="sm" style={SECTION_CARD_STYLE}>
            <Group gap={6} mb={12} align="center" justify="space-between" wrap="nowrap">
              <Text size="xs" fw={600}>
                Current assignees
                {assignmentsLoading
                  ? '…'
                  : currentAssigneesSummary
                    ? ` (${currentAssigneesSummary})`
                    : ''}
              </Text>
              {relevantExisting.length > ASSIGNEES_COLLAPSE_AT ? (
                <UnstyledButton
                  type="button"
                  onClick={() => setAssigneesExpanded((v) => !v)}
                >
                  <Text size="xs" c="dimmed" fw={600}>
                    {assigneesExpanded ? 'Hide' : 'Show'}
                  </Text>
                </UnstyledButton>
              ) : null}
            </Group>
            {relevantExisting.length === 0 ? (
              <Text size="xs" c="dimmed">
                No assignments for the current targets yet.
              </Text>
            ) : assigneesExpanded ||
              relevantExisting.length <= ASSIGNEES_COLLAPSE_AT ? (
              <Stack gap={4}>
                {(assigneesExpanded
                  ? relevantExisting
                  : visibleAssignees
                ).map((row) => {
                  const showSource =
                    Boolean(row.sourceLabel) && row.sourceLabel !== 'This task';
                  const primaryTitle = [
                    row.userName || row.userId,
                    roleLabel(String(row.role)),
                    showSource ? row.sourceLabel : null,
                  ]
                    .filter(Boolean)
                    .join(' — ');
                  return (
                  <Group
                    key={row.id}
                    justify="space-between"
                    wrap="nowrap"
                    gap="xs"
                    align="flex-start"
                    style={{ minWidth: 0 }}
                  >
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="xs" truncate title={primaryTitle}>
                        <Text span size="xs" fw={600}>
                          {row.userName || row.userId}
                        </Text>
                        {' — '}
                        <Text
                          span
                          size="xs"
                          fw={600}
                          style={{ color: assignRoleColor(String(row.role)).fg }}
                        >
                          {roleLabel(String(row.role))}
                        </Text>
                        {row.status !== 'active' ? (
                          <Text span size="xs" c="dimmed">
                            {' '}
                            · inactive
                          </Text>
                        ) : null}
                      </Text>
                      {showSource ? (
                        <Text size="xs" c="dimmed" truncate title={row.sourceLabel}>
                          {row.sourceLabel}
                        </Text>
                      ) : null}
                    </Box>
                    <Group gap={4} wrap="nowrap" align="flex-start" style={{ flexShrink: 0 }}>
                      <Tooltip label={row.status === 'active' ? 'Active' : 'Inactive'} withArrow>
                        <Checkbox
                          size="xs"
                          mt={2}
                          aria-label={
                            row.status === 'active'
                              ? 'Deactivate assignment'
                              : 'Activate assignment'
                          }
                          checked={row.status === 'active'}
                          disabled={statusBusyId === row.id}
                          onChange={() => void handleToggleStatus(row)}
                        />
                      </Tooltip>
                      <Tooltip label="Delete assignment" withArrow>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          mt={0}
                          aria-label={`Delete assignment for ${row.userName || row.userId}`}
                          disabled={statusBusyId === row.id}
                          onClick={() => void handleDeleteAssignment(row)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                  );
                })}
              </Stack>
            ) : null}
          </Paper>

          <MultiSelect
            size="xs"
            label="Users"
            placeholder={usersLoading ? 'Loading users…' : 'Select users'}
            data={userOptions}
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            searchable
            clearable
            disabled={usersLoading}
            nothingFoundMessage="No users"
            error={userFieldError}
            comboboxProps={{ withinPortal: true, zIndex: dropdownZIndex }}
          />

          <Select
            size="xs"
            label="Role"
            data={ASSIGN_ROLE_OPTIONS}
            value={role}
            onChange={(v) => setRole((v as UserAssignmentRole) || null)}
            allowDeselect={false}
            leftSection={
              role ? (
                <Box
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: assignRoleColor(role).bg,
                    flexShrink: 0,
                  }}
                />
              ) : undefined
            }
            renderOption={({ option }) => {
              const colors = assignRoleColor(option.value);
              return (
                <Group gap={8} wrap="nowrap">
                  <Box
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: colors.bg,
                      flexShrink: 0,
                    }}
                  />
                  <Text size="sm">{option.label}</Text>
                </Group>
              );
            }}
            comboboxProps={{ withinPortal: true, zIndex: dropdownZIndex }}
          />
        </>
      ) : null}
    </Stack>
  );

  const footer = (
    <Stack
      gap={6}
      style={{
        flexShrink: 0,
        padding: '10px 16px',
        borderTop: '1px solid var(--mantine-color-default-border)',
        background: DRAWER_SURFACE,
      }}
    >
      {confirmSummary ? (
        <Text size="xs" c="dimmed">
          {confirmSummary}
        </Text>
      ) : null}
      {otherScopePendingNote ? (
        <Text size="xs" c="orange.7">
          {otherScopePendingNote}
        </Text>
      ) : null}
      <Group justify="flex-end" gap="xs">
        <Button size="xs" variant="default" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Tooltip
          label={disabledReason ?? 'Create assignments'}
          disabled={canSubmit || !disabledReason}
          position="top-end"
        >
          <Box component="span" style={{ display: 'inline-flex' }}>
            <Button
              size="xs"
              loading={submitting}
              disabled={!canSubmit}
              onClick={() => void handleAssign()}
            >
              {assignCtaLabel}
            </Button>
          </Box>
        </Tooltip>
      </Group>
    </Stack>
  );

  const panelBody = (
    <Box
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 'var(--mantine-spacing-md)',
        }}
      >
        {scrollBody}
      </Box>
      {footer}
    </Box>
  );

  if (isModal) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={titleBlock}
        centered
        size="md"
        radius="md"
        padding={0}
        zIndex={MODAL_Z_INDEX}
        closeOnClickOutside={!submitting}
        closeOnEscape={!submitting}
        portalProps={modalContainer ? { target: modalContainer } : undefined}
        styles={{
          content: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '85vh',
            backgroundColor: DRAWER_SURFACE,
          },
          header: {
            flexShrink: 0,
            padding: '12px 16px',
            borderBottom: '1px solid var(--mantine-color-default-border)',
            marginBottom: 0,
            backgroundColor: DRAWER_SURFACE,
            alignItems: 'flex-start',
          },
          title: {
            flex: 1,
            marginRight: 8,
            minWidth: 0,
          },
          close: {
            alignSelf: 'flex-start',
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            height: MODAL_BODY_HEIGHT,
            overflow: 'hidden',
            backgroundColor: DRAWER_SURFACE,
          },
        }}
      >
        {panelBody}
      </Modal>
    );
  }

  if (shell === 'floating') {
    if (!opened) return null;
    return createPortal(
      <div
        role="dialog"
        aria-label="Assign users"
        style={{
          position: 'fixed',
          left: floatPosition.x,
          top: floatPosition.y,
          width: floatSize.width,
          height: floatSize.height,
          zIndex: FLOATING_Z_INDEX,
          display: 'flex',
          flexDirection: 'column',
          background: DRAWER_SURFACE,
          border: '1px solid var(--mantine-color-default-border)',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          minWidth: MIN_PANEL_WIDTH,
          minHeight: MIN_PANEL_HEIGHT,
        }}
      >
        <Group
          justify="space-between"
          wrap="nowrap"
          align="flex-start"
          px="md"
          gap="xs"
          style={{
            minHeight: HEADER_HEIGHT,
            borderBottom: '1px solid var(--mantine-color-default-border)',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            flexShrink: 0,
            paddingTop: 12,
            paddingBottom: 12,
          }}
          onMouseDown={onMouseDownHeader}
        >
          {titleBlock}
          <Group
            gap={6}
            wrap="nowrap"
            align="flex-start"
            style={{ flexShrink: 0, paddingTop: 1 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {shellMenu}
            <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close">
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>
        {panelBody}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startY = e.clientY;
            const startW = floatSize.width;
            const startH = floatSize.height;
            const onMove = (ev: MouseEvent) => {
              setFloatSize({
                width: Math.max(MIN_PANEL_WIDTH, startW + (ev.clientX - startX)),
                height: Math.max(MIN_PANEL_HEIGHT, startH + (ev.clientY - startY)),
              });
            };
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: 14,
            height: 14,
            cursor: 'nwse-resize',
          }}
          aria-hidden
        />
      </div>,
      document.body,
    );
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={DRAWER_SIZE}
      padding={0}
      withOverlay={false}
      trapFocus={false}
      lockScroll={false}
      closeOnClickOutside={false}
      title={
        <Group
          gap="sm"
          wrap="nowrap"
          justify="space-between"
          align="flex-start"
          style={{ width: '100%', minWidth: 0 }}
        >
          {titleBlock}
          {headerActions}
        </Group>
      }
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: DRAWER_SURFACE,
          boxShadow:
            '-18px 0 36px rgba(15, 23, 42, 0.18), -4px 0 10px rgba(15, 23, 42, 0.12)',
          borderLeft: '1px solid var(--mantine-color-default-border)',
        },
        header: {
          flexShrink: 0,
          backgroundColor: DRAWER_SURFACE,
          padding: '12px 16px',
          alignItems: 'flex-start',
        },
        title: {
          flex: 1,
          marginRight: 8,
          minWidth: 0,
        },
        close: {
          marginTop: 0,
          alignSelf: 'flex-start',
        },
        body: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: DRAWER_SURFACE,
          padding: 0,
          overflow: 'hidden',
        },
      }}
      portalProps={modalContainer ? { target: modalContainer } : undefined}
      classNames={shellDrawerClassNames}
    >
      {panelBody}
    </Drawer>
  );
}
